import { memo } from "react";

import { type SelectedOption } from "@shopify/hydrogen-react/storefront-api-types";

import { Button } from "@react/components/Button";
import { Heading } from "@react/components/Heading";
import { Image } from "@react/components/Image";
import { Price } from "@react/components/Price";
import { Text } from "@react/components/Text";
import { isColorOption } from "@react/utilities";
import { type CartProduct, type CartProductVariant } from "@tablescape/state";
import { type TTailwindString, tw } from "classnames";

const styles = {
  wrapper: tw(
    "box-border",
    "flex",
    "w-[295px]" as TTailwindString,
    "h-full",
    "flex-shrink-0",
    "bg-white",
    "rounded-[4px]" as TTailwindString,
    "px-2",
    "py-2.5"
  ),
  image: tw(
    "flex",
    "items-center",
    "justify-center",
    "mr-2",
    "h-[106px]" as TTailwindString,
    "w-[84px]" as TTailwindString,
    "bg-background-secondary"
  ),
  content: tw("flex", "flex-1", "flex-col", "justify-between"),
  contentText: tw("flex-1"),
  contentFooterContainer: tw("flex", "justify-between"),
  contentFooter: tw("flex", "flex-1", "gap-1", "w-full", "justify-end"),
  selectedOptionSwatch: (handle: string) =>
    tw(
      "flex",
      `bg-product-${handle}` as TTailwindString,
      "rounded-full",
      "overflow-hidden",
      "w-[14px]" as TTailwindString,
      "h-[14px]" as TTailwindString
    ),
  selectedOptionValue: tw(
    "flex",
    "flex-1",
    "items-center",
    "font-normal",
    "normal-case",
    "tracking-normal",
    "whitespace-nowrap"
  ),
  footerCta: tw("flex", "flex-shrink-0", "w-18"),
};

type Props = {
  handleOpenProductVariantDrawer: () => void;
  handleUpsellAddToCart: () => void;
  isLoading: boolean;
  selectedOption: (SelectedOption & { handle: string }) | undefined;
  selectedVariant: CartProductVariant;
  upsellProduct: CartProduct;
};

const CartUpsellItemViewBase = ({
  handleOpenProductVariantDrawer,
  handleUpsellAddToCart,
  isLoading,
  selectedOption,
  selectedVariant,
  upsellProduct,
}: Props) => {
  return (
    <div className={styles.wrapper}>
      {selectedVariant.imageUrl && (
        <div className={styles.image}>
          <Image height={84} src={selectedVariant.imageUrl} width={84} />
        </div>
      )}
      <div className={styles.content}>
        <Heading as="h6">{upsellProduct.title}</Heading>
        <div className={styles.contentText}>
          <Text as="caption">{upsellProduct.description}</Text>
        </div>
        <div>
          <Price
            as="body2"
            compareAtPrice={selectedVariant.compareAtPrice}
            compareAtPriceDisplay={selectedVariant.compareAtPriceDisplay}
            price={selectedVariant.price}
            showCurrencySuffix={false}
          />
        </div>
        <div className={styles.contentFooterContainer}>
          <div className={styles.contentFooter}>
            {selectedOption && (
              <Button
                fluid
                iconName="arrowhead"
                iconPosition="end"
                mtrTrack={false}
                onClick={handleOpenProductVariantDrawer}
                type="pill-outline"
              >
                {isColorOption(selectedOption) && (
                  <div
                    className={styles.selectedOptionSwatch(
                      selectedOption.handle
                    )}
                    role="presentation"
                  ></div>
                )}
                <div className={styles.selectedOptionValue}>
                  {selectedOption.value}
                </div>
              </Button>
            )}

            <div className={styles.footerCta}>
              <Button
                fluid
                isLoading={isLoading}
                mtrTrack={false}
                onClick={handleUpsellAddToCart}
                type="pill-char"
              >
                Add
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const CartUpsellItemView = memo(CartUpsellItemViewBase);
